import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import './UnderwritingCoversIssued.css';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusBadge = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['completed', 'paid', 'success'].includes(normalized)) {
    return <span className="badge badge-success">Paid</span>;
  }
  if (normalized === 'pending') {
    return <span className="badge badge-pending">Pending</span>;
  }
  if (normalized === 'failed') {
    return <span className="badge badge-failed">Failed</span>;
  }
  return <span className="badge badge-default">{status || '—'}</span>;
};

const isInvalidDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  return start > end;
};

const AdminUnderwritingIssuedCovers = () => {
  const API_BASE_URL = getApiBaseUrl();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUnderwriter, setSelectedUnderwriter] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [underwriterOptions, setUnderwriterOptions] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filterVersion, setFilterVersion] = useState(0);
  const [hasInitializedAutoApply, setHasInitializedAutoApply] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasInitializedSearchAutoApply, setHasInitializedSearchAutoApply] = useState(false);
  const [hasInitializedDateAutoApply, setHasInitializedDateAutoApply] = useState(false);

  const fetchData = async (pageNum = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(selectedUnderwriter && { underwriterId: selectedUnderwriter }),
        ...(selectedAgency && { agencyId: selectedAgency }),
        ...(selectedStatus && { status: selectedStatus })
      });
      const response = await fetch(`${API_BASE_URL}/api/admin/underwriting/issued-covers-all?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        setRows([]);
        setError(result?.error || 'Failed to load issued covers');
        return;
      }
      setRows(result.data?.business || []);
      setPagination(result.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (err) {
      setRows([]);
      setError(err.message || 'Failed to load issued covers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
  }, [page, filterVersion]);

  useEffect(() => {
    const nextSearch = search.trim();
    const timer = setTimeout(() => {
      setDebouncedSearch(nextSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!hasInitializedSearchAutoApply) {
      setHasInitializedSearchAutoApply(true);
      return;
    }
    setPage(1);
    setFilterVersion((v) => v + 1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!hasInitializedAutoApply) {
      setHasInitializedAutoApply(true);
      return;
    }
    setPage(1);
    setFilterVersion((v) => v + 1);
  }, [selectedUnderwriter, selectedAgency, selectedStatus]);

  useEffect(() => {
    if (!hasInitializedDateAutoApply) {
      setHasInitializedDateAutoApply(true);
      return;
    }
    if (!startDate || !endDate) return;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    if (start > end) return;
    setPage(1);
    setFilterVersion((v) => v + 1);
  }, [startDate, endDate]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingFilters(true);
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch(`${API_BASE_URL}/api/admin/underwriting/issued-covers-all/filters`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const result = await response.json();
        if (response.ok && result?.success) {
          setUnderwriterOptions(result.data?.underwriters || []);
          setAgencyOptions(result.data?.agencies || []);
          setStatusOptions(result.data?.statuses || []);
        }
      } catch (_) {
        // Keep page usable even if dropdown options fail to load
      } finally {
        setIsLoadingFilters(false);
      }
    };

    fetchFilterOptions();
  }, [API_BASE_URL]);

  const onFilter = () => {
    setPage(1);
    setFilterVersion((v) => v + 1);
  };

  const onClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStartDate('');
    setEndDate('');
    setSelectedUnderwriter('');
    setSelectedAgency('');
    setSelectedStatus('');
    setPage(1);
    setFilterVersion((v) => v + 1);
  };

  const showInvalidDateRange = isInvalidDateRange(startDate, endDate);

  return (
    <div className="underwriting-covers-issued">
      <div className="filters-row">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search cover no, client, underwriter, agency..."
          className="filter-input"
        />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="filter-input"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="filter-input"
        />
        <select
          value={selectedUnderwriter}
          onChange={(e) => setSelectedUnderwriter(e.target.value)}
          className="filter-input"
          disabled={isLoadingFilters}
        >
          <option value="">All Underwriters</option>
          {underwriterOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={selectedAgency}
          onChange={(e) => setSelectedAgency(e.target.value)}
          className="filter-input"
          disabled={isLoadingFilters}
        >
          <option value="">All Agencies</option>
          {agencyOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="filter-input"
          disabled={isLoadingFilters}
        >
          <option value="">All Statuses</option>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <button type="button" className="btn-filter" onClick={onFilter}>Filter</button>
        <button type="button" className="btn-filter" onClick={onClearFilters}>Clear</button>
      </div>
      {showInvalidDateRange && (
        <div className="error-alert">End date must be after or equal to start date.</div>
      )}

      {error && <div className="error-alert">{error}</div>}

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner-large" />
          <p>Loading issued covers...</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No issued covers found</h3>
          <p>Covers issued by underwriting users will appear here.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="covers-table">
              <thead>
                <tr>
                  <th>Cover No.</th>
                  <th>Issue Date/Time</th>
                  <th>Issued By</th>
                  <th>Agency</th>
                  <th>Status</th>
                  <th>Product/Cover Type</th>
                  <th>Client Name</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.cover_no || '—'}</td>
                    <td>{formatDateTime(row.issue_datetime)}</td>
                    <td>{row.issued_by_name || '—'}</td>
                    <td>{row.agency_name || '—'}</td>
                    <td>{getStatusBadge(row.status)}</td>
                    <td>{row.cover_type || '—'}</td>
                    <td>{row.insured_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUnderwritingIssuedCovers;
